import { AlertTriangle, Download, FileDown, Printer, ReceiptText } from "lucide-react";
import { useMemo } from "react";
import { buildStatement, deducted, inflowLabelKey } from "../../shared/statement";
import type { DashboardData, SessionUser } from "../types";
import { formatCredits, formatDate, monthLabel } from "../utils";
import { useServiceText } from "../service-text";
import { useT } from "../i18n";
import MonthClosePanel from "../components/MonthClosePanel";

/**
 * The monthly statement.
 *
 * Why it exists: a retainer client opens a portal to answer one question — "what
 * did I burn this month, and what do I have left?" Until now the only answer was a
 * raw CSV of ledger rows, which is a database export, not a statement.
 *
 * It deliberately adds NO new API. Every figure below already ships in
 * /api/dashboard; inventing a second server-side path to the same numbers is how a
 * statement and a balance start disagreeing with each other.
 */

export default function StatementPage({
  data, user, onChanged,
}: {
  data: DashboardData;
  user: SessionUser;
  /** The ledger moves when a month closes, so the workspace reloads. */
  onChanged: () => void;
}) {
  const t = useT();
  const st = useServiceText();
  const kd = (credits: number) => credits * data.account.creditValueKd;

  // The arithmetic lives in shared/statement.ts so the PDF at /api/statement.pdf
  // renders the same figures from the same code. A printed statement that disagrees
  // with the screen it came from is worse than no PDF at all.
  const statement = useMemo(() => buildStatement(data), [data]);

  const deductions = useMemo(
    () => data.entries.filter((entry) => entry.kind === "spend").sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)),
    [data.entries],
  );

  const committedRequests = useMemo(
    () => data.requests.filter((item) => item.status === "approved" || item.status === "in_progress"),
    [data.requests],
  );

  const hasActivity = statement.spent > 0 || deductions.length > 0 || committedRequests.length > 0;

  return (
    <div className="page-stack statement-page">
      {/* On the statement because the statement IS the month's closing document.
          Sending somebody elsewhere to sign off a figure they are looking at is how
          a feature ends up unused. */}
      <MonthClosePanel month={data.month} user={user} onChanged={onChanged} />

      <section className="table-toolbar statement-toolbar no-print">
        <div>
          <span className="section-eyebrow">{monthLabel(data.month)}</span>
          <h3 className="statement-toolbar-title">{t("statement.heading")}</h3>
        </div>
        <div className="statement-actions">
          <button className="button button-secondary" onClick={() => window.print()}>
            <Printer size={17} /> {t("common.print")}
          </button>
          <a className="button button-secondary" href={`/api/export/ledger.csv?month=${data.month}`}>
            <Download size={17} /> {t("common.exportCsv")}
          </a>
          {/* A real file from the server, not the browser's print dialogue. Print-to-PDF
              produced a different document on every machine — someone else's headers,
              a page break through the totals — around a statement that goes to Zain's
              finance team. Print stays for anyone who genuinely wants paper. */}
          <a className="button button-primary" href={`/api/statement.pdf?month=${data.month}`}>
            <FileDown size={17} /> {t("statement.downloadPdf")}
          </a>
        </div>
      </section>

      {/* Print-only letterhead. On screen the AppShell already says whose account
          this is; on paper the page leaves the app and has to introduce itself. */}
      <header className="statement-letterhead print-only">
        <div>
          <strong>{data.account.name}</strong>
          <span>{t("statement.heading")} — {monthLabel(data.month)}</span>
        </div>
        <div className="statement-letterhead-meta">
          <span>{t("statement.retainer")}</span>
          <span>1 credit = {formatCredits(data.account.creditValueKd)} KD</span>
        </div>
      </header>

      {!statement.reconciled && (
        <section className="statement-warning" role="alert">
          <AlertTriangle size={18} />
          <div>
            <strong>{t("statement.unreconciled")}</strong>
            <p>{t("statement.driftBody", {
              statementBalance: formatCredits(statement.closing),
              accountBalance: formatCredits(data.balance.available),
              drift: formatCredits(statement.drift),
            })}</p>
          </div>
        </section>
      )}

      <section className="panel statement-summary">
        <div className="panel-header compact">
          <div>
            <span className="section-eyebrow">{monthLabel(data.month)}</span>
            <h3>{t("statement.summary")}</h3>
          </div>
          <span>{data.account.name}</span>
        </div>

        <table className="data-table statement-table">
          <thead>
            <tr><th>{t("table.movement")}</th><th className="numeric">{t("table.credits")}</th><th className="numeric">{t("table.value")}</th></tr>
          </thead>
          <tbody>
            {statement.inflows.map((row) => (
              // A zero row is kept, not hidden. "Additional credits purchased: 0"
              // answers a question; a missing line makes the reader wonder whether
              // it was zero or simply left out.
              <tr key={row.kind}>
                <td data-label={t("col.movement")}>{t(inflowLabelKey(row.kind))}</td>
                <td data-label={t("col.credits")} className="numeric">{row.credits > 0 ? "+" : ""}{formatCredits(row.credits)}</td>
                <td data-label={t("col.value")} className="numeric">{formatCredits(kd(row.credits))} KD</td>
              </tr>
            ))}
            <tr className="statement-subtotal">
              <td data-label={t("col.movement")}><strong>{t("statement.availableThisMonth")}</strong></td>
              <td data-label={t("col.credits")} className="numeric"><strong>{formatCredits(statement.creditsIn)}</strong></td>
              <td data-label={t("col.value")} className="numeric"><strong>{formatCredits(kd(statement.creditsIn))} KD</strong></td>
            </tr>
            <tr>
              <td data-label={t("col.movement")}>{t("statement.billedThisMonth")}</td>
              <td data-label={t("col.credits")} className="numeric credit-negative">{deducted(statement.spent, formatCredits)}</td>
              <td data-label={t("col.value")} className="numeric credit-negative">{deducted(kd(statement.spent), formatCredits)} KD</td>
            </tr>
            <tr>
              <td data-label={t("col.movement")}>
                {t("statement.approvedNotDelivered")}
                <span className="statement-hint">{t("statement.heldNote")}</span>
              </td>
              <td data-label={t("col.credits")} className="numeric">{deducted(statement.committed, formatCredits)}</td>
              <td data-label={t("col.value")} className="numeric">{deducted(kd(statement.committed), formatCredits)} KD</td>
            </tr>
            <tr className="statement-total">
              <td data-label={t("col.movement")}><strong>{t("statement.carryingForward")}</strong></td>
              <td data-label={t("col.credits")} className="numeric"><strong>{formatCredits(statement.closing)}</strong></td>
              <td data-label={t("col.value")} className="numeric"><strong>{formatCredits(kd(statement.closing))} KD</strong></td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="panel">
        <div className="panel-header compact">
          <div><span className="section-eyebrow">{t("statement.detail")}</span><h3>{t("statement.workBilled")}</h3></div>
          <span>{t("statement.itemCount", { n: deductions.length })}</span>
        </div>
        {deductions.length ? (
          <div className="responsive-table-wrap">
            <table className="data-table statement-table">
              <thead>
                <tr><th>{t("table.date")}</th><th>{t("table.work")}</th><th>{t("table.service")}</th><th>{t("table.approvedBy")}</th><th className="numeric">{t("table.credits")}</th><th className="numeric">{t("table.value")}</th></tr>
              </thead>
              <tbody>
                {deductions.map((entry) => (
                  <tr key={entry.id}>
                    <td data-label={t("col.date")}>{formatDate(entry.occurred_at)}</td>
                    <td data-label={t("table.work")}><strong>{entry.title}</strong>{entry.note ? <span>{entry.note}</span> : null}</td>
                    <td data-label={t("table.service")}>{st.name(entry.service_id, entry.service_name) || t("ledger.accountCredits")}</td>
                    <td data-label={t("col.approvedBy")}>{entry.created_by_name || t("ledger.systemActor")}</td>
                    <td data-label={t("col.credits")} className="numeric">{formatCredits(Math.abs(entry.credits))}</td>
                    <td data-label={t("col.value")} className="numeric">{formatCredits(kd(Math.abs(entry.credits)))} KD</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <ReceiptText size={30} />
            <h4>{t("statement.nothingBilled", { month: monthLabel(data.month) })}</h4>
            <p>
              {hasActivity
                ? t("statement.nothingDeducted")
                : t("statement.fullBalanceCarries")}
            </p>
          </div>
        )}
      </section>

      {committedRequests.length > 0 && (
        <section className="panel">
          <div className="panel-header compact">
            <div><span className="section-eyebrow">{t("statement.inFlight")}</span><h3>{t("statement.approvedNotDelivered")}</h3></div>
            <span>{t("statement.creditsHeld", { n: formatCredits(statement.committed) })}</span>
          </div>
          <div className="responsive-table-wrap">
            <table className="data-table statement-table">
              <thead>
                <tr><th>{t("table.requested")}</th><th>{t("table.work")}</th><th>{t("table.status")}</th><th>{t("table.due")}</th><th className="numeric">{t("table.credits")}</th></tr>
              </thead>
              <tbody>
                {committedRequests.map((item) => (
                  <tr key={item.id}>
                    <td data-label={t("col.requested")}>{formatDate(item.created_at)}</td>
                    <td data-label={t("table.work")}><strong>{item.title}</strong><span>{st.name(item.service_id, item.service_name)}</span></td>
                    <td data-label={t("col.status")}>{t(item.status === "in_progress" ? "gallery.inProgress" : "status.approved")}</td>
                    <td data-label={t("col.due")}>{item.due_date ? formatDate(item.due_date) : t("common.notSet")}</td>
                    <td data-label={t("col.credits")} className="numeric">{formatCredits(item.final_credits ?? item.estimated_credits)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data.categorySpend.length > 0 && (
        <section className="panel">
          <div className="panel-header compact">
            <div><span className="section-eyebrow">{t("statement.breakdown")}</span><h3>{t("statement.whereWent")}</h3></div>
          </div>
          <div className="responsive-table-wrap">
            <table className="data-table statement-table">
              <thead>
                <tr><th>{t("table.category")}</th><th className="numeric">{t("table.credits")}</th><th className="numeric">{t("table.value")}</th><th className="numeric">{t("table.share")}</th></tr>
              </thead>
              <tbody>
                {data.categorySpend.map((row) => (
                  <tr key={row.category}>
                    <td data-label={t("col.category")}>{st.category(row.category)}</td>
                    <td data-label={t("col.credits")} className="numeric">{formatCredits(row.credits)}</td>
                    <td data-label={t("col.value")} className="numeric">{formatCredits(kd(row.credits))} KD</td>
                    <td data-label={t("col.share")} className="numeric">
                      {statement.spent > 0 ? `${Math.round((row.credits / statement.spent) * 100)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <footer className="statement-footnote">
        {t("statement.footnote", {
          rate: formatCredits(data.account.creditValueKd),
          start: formatDate(data.account.contractStart),
          end: formatDate(data.account.contractEnd),
        })}
      </footer>
    </div>
  );
}
